<template>
  <!--
    Consolidates InputSelect.vue and AppListboxSelect.vue's capabilities into one Mealz-facing Select API.
    The accessible widget itself is Nuxt UI's, reached through VendorSelect.base.vue.
  -->
  <VendorSelect
    :id="id"
    :class="structure['root']"
    :model-value="modelValue"
    :options="options"
    :placeholder="placeholder"
    :disabled="disabled || loading"
    :loading="loading"
    :required="required"
    :all-label="allLabel"
    :invalid="invalid"
    :multiple="multiple"
    :size="size"
    :ariaLabel="ariaLabel"
    @update:model-value="handleUpdate"
  >
    <template
      v-if="loading && loadingLabel !== ''"
      #default
      >{{ loadingLabel }}</template
    >
  </VendorSelect>
</template>

<script setup lang="ts" generic="TValue extends number | string | (number | string)[]">
import VendorSelect, { type VendorSelectSize } from '../../base/VendorSelect.base.vue'

import structure from './Select.structure.module.css'

/** A leading image for an option; an omitted `src` draws a placeholder. */
export interface SelectOptionAvatar {
  src?: string
  alt: string
}

/** One selectable choice. `description` renders as secondary copy under the label. */
export interface SelectOption {
  value: number | string
  label: string
  description?: string
  avatar?: SelectOptionAvatar
  disabled?: boolean
}

/** A non-selectable heading, for an options list divided into groups. */
export interface SelectGroupLabel {
  type: 'label'
  label: string
  /** Unsettable: a heading carries no value. */
  value?: never
}

/** What `options` may hold: choices, optionally divided by headings. */
export type SelectItem = SelectGroupLabel | SelectOption

/** `allLabel` offers a single fixed choice, so it cannot accompany `multiple`. */
type SelectModeProps =
  | {
      /** Non-empty prepends the one fixed "All" choice, whose value is ALL_OPTION_VALUE; the options follow it. */
      allLabel?: string
      /** Selects several values instead of one. Pair with an array model. */
      multiple?: false
    }
  | {
      /** Unsettable alongside `multiple`. */
      allLabel?: never
      /** Selects several values instead of one. Pair with an array model. */
      multiple: true
    }

interface SelectBaseProps<TModel> {
  /** Selected value; an array selects several, matching `multiple`. Empty means nothing selected. */
  modelValue: TModel
  /** The choices, in render order, optionally divided by headings. */
  options: SelectItem[]
  /** Shown while nothing is selected. Translated by the caller. */
  placeholder?: string
  /** Id for the trigger; bind the id a FormField hands its control slot. Empty supplies its own. */
  id?: string
  /** Prevents interaction. Loading implies it. */
  disabled?: boolean
  /** Shows asynchronous feedback while options are fetched, and blocks interaction until they arrive. */
  loading?: boolean
  /** Replaces the trigger text while loading; empty leaves the vendor's spinner to carry the state. */
  loadingLabel?: string
  /** Marks the control as mandatory. */
  required?: boolean
  /** Paints the error state. Bind the FormField control slot's `invalid`. */
  invalid?: boolean
  /** Accessible name for the control, kept apart from the chosen value; name the filter or field. */
  ariaLabel?: string
  /** Control height; match the neighbouring inputs. */
  size?: VendorSelectSize
}

withDefaults(defineProps<SelectBaseProps<TValue> & SelectModeProps>(), {
  allLabel: '',
  ariaLabel: '',
  disabled: false,
  id: '',
  invalid: false,
  loading: false,
  loadingLabel: '',
  placeholder: '',
  required: false,
  size: 'md'
})

const emit = defineEmits<{ 'update:modelValue': [value: TValue] }>()

const handleUpdate = (value: TValue) => {
  emit('update:modelValue', value)
}
</script>
